import { inject, Injectable } from '@angular/core';
import {
  BulkDeleteOtAgentsGQL,
  BulkDeleteOtAgentsMutation,
  BulkDeleteOtAgentsMutationVariables,
  BulkOtAgentsScheduleEditGQL,
  BulkOtAgentsScheduleEditMutation,
  BulkOtAgentsScheduleEditMutationVariables,
  CheckForAllOtAgentUpdatesGQL,
  CheckForAllOtAgentUpdatesQuery,
  CheckForOtAgentUpdatesGQL,
  CheckForOtAgentUpdatesQuery,
  CheckForOtAgentUpdatesQueryVariables,
  CreatePairingKeyGQL,
  CreatePairingKeyMutation,
  CreatePairingKeyMutationVariables,
  DeleteOtAgentGQL,
  DeleteOtAgentMutation,
  DeleteOtAgentMutationVariables,
  EditOtAgentGQL,
  EditOtAgentMutation,
  EditOtAgentMutationVariables,
  GetOtAgentGQL,
  GetOtAgentQuery,
  GetOtAgentQueryVariables,
  GetOtAgentsGQL,
  GetOtAgentsGroupedGQL,
  GetOtAgentsGroupedQueryVariables,
  JobFragment,
  OtAgentActionGQL,
  OtAgentActionMutation,
  OtAgentActionMutationVariables,
  OtAgentDetailsFragment, OtAgentSelectField,
  PairingKeyResponse,
} from '@uigy/generated/graphql';
import { map, take, tap } from 'rxjs/operators';
import { GetOtAgentsQueryVariables } from '@uigy/generated/graphql-worker';
import { Observable, Subject, timer } from 'rxjs';
import Utils from '@uigy/utils';
import { JobsV2Service } from '@uigy/shared/services/jobs-v2/jobs-v2.service';
import { JobComplete } from '@uigy/shared/services/jobs-v2/jobs-v2.utils';
import { TimeUnits } from '@uigy/shared/enums/timeUnits.enum';

@Injectable({
  providedIn: 'root'
})
export class OtAgentsService {
  private getOtAgentsGQL = inject(GetOtAgentsGQL);
  private getOtAgentsGroupedGQL = inject(GetOtAgentsGroupedGQL);
  private getOtAgentGQL = inject(GetOtAgentGQL);
  private editOtAgentGQL = inject(EditOtAgentGQL);
  private deleteOtAgentGQL = inject(DeleteOtAgentGQL);
  private bulkDeleteOtAgentsGQL = inject(BulkDeleteOtAgentsGQL);
  private otAgentActionGQL = inject(OtAgentActionGQL);
  private bulkOtAgentsScheduleEditGQL = inject(BulkOtAgentsScheduleEditGQL);
  private checkForOtAgentUpdatesGQL = inject(CheckForOtAgentUpdatesGQL);
  private checkForAllOtAgentUpdatesGQL = inject(CheckForAllOtAgentUpdatesGQL);
  private createPairingKeyGQL = inject(CreatePairingKeyGQL);
  private jobsService = inject(JobsV2Service);

  constructor() {}

  public getOtAgents(vars: GetOtAgentsQueryVariables) {
    return this.getOtAgentsGQL.fetch(vars).pipe(
      take(1)
    );
  }

  public getOtAgentsGrouped(vars: GetOtAgentsGroupedQueryVariables) {
    return this.getOtAgentsGroupedGQL.fetch(vars).pipe(
      take(1)
    );
  }

  public getOtAgent(vars: GetOtAgentQueryVariables): Observable<OtAgentDetailsFragment> {
    return this.getOtAgentGQL.fetch(vars).pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: GetOtAgentQuery) => response.otAgent)
    );
  }

  public editOtAgent(vars: EditOtAgentMutationVariables, successMessage: string): Observable<JobComplete> {
    return this.editOtAgentGQL.mutate(vars).pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: EditOtAgentMutation) => response.editOtAgent),
      this.jobsService.handleJobOperator(),
      this.jobsService.simpleNotificationsOperator({success: successMessage})
    );
  }

  public deleteOtAgent(vars: DeleteOtAgentMutationVariables, successMessage: string): Observable<JobComplete> {
    return this.deleteOtAgentGQL.mutate(vars).pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: DeleteOtAgentMutation) => response.deleteOtAgent),
      this.jobsService.handleJobOperator(),
      this.jobsService.simpleNotificationsOperator({success: successMessage})
    );
  }

  public bulkDeleteOtAgents(vars: BulkDeleteOtAgentsMutationVariables): Observable<JobComplete> {
    return this.bulkDeleteOtAgentsGQL.mutate(vars).pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: BulkDeleteOtAgentsMutation) => response.bulkDeleteOtAgents),
      this.jobsService.handleJobOperator()
    );
  }

  public otAgentAction(vars: OtAgentActionMutationVariables, successMessage?: string, refetchData$?: Subject<void>): Observable<JobComplete> {
    return this.otAgentActionGQL.mutate(vars).pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: OtAgentActionMutation) => response.otAgentAction),
      tap(() => {
        if (refetchData$) {
          // Give some time for the backend to process the action before refetching data
          timer(TimeUnits.twoSeconds).subscribe(() => {
            refetchData$.next();
          });
        }
      }),
      this.jobsService.handleJobOperator(),
      this.jobsService.simpleNotificationsOperator({success: successMessage})
    );
  }

  public bulkOtAgentsScheduleEdit(vars: BulkOtAgentsScheduleEditMutationVariables, successMessage?: string): Observable<JobComplete> {
    return this.bulkOtAgentsScheduleEditGQL.mutate(vars).pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: BulkOtAgentsScheduleEditMutation) => response.bulkOtAgentsScheduleEdit),
      this.jobsService.handleJobOperator(),
      this.jobsService.simpleNotificationsOperator({success: successMessage})
    );
  }

  public checkForOtAgentUpdates(vars: CheckForOtAgentUpdatesQueryVariables): Observable<JobFragment> {
    return this.checkForOtAgentUpdatesGQL.fetch(vars).pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: CheckForOtAgentUpdatesQuery) => response.checkForOtAgentUpdates)
    );
  }

  public checkForAllOtAgentUpdates(): Observable<JobFragment> {
    return this.checkForAllOtAgentUpdatesGQL.fetch().pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: CheckForAllOtAgentUpdatesQuery) => response.checkForAllOtAgentUpdates)
    );
  }

  public createPairingKey(vars: CreatePairingKeyMutationVariables): Observable<PairingKeyResponse> {
    return this.createPairingKeyGQL.mutate(vars).pipe(
      take(1),
      Utils.fetchResultOperator(),
      map((response: CreatePairingKeyMutation) => response.createPairingKey)
    );
  }

  public getScoutUpdateVersion(): Observable<string> {
    return this.getOtAgentsGroupedGQL.fetch({
      first: 1,
      fields: [
        {
          field: OtAgentSelectField.ScoutAvailableVersion,
          name: 'scoutAvailableVersion'
        }
      ],
      grouping: [
        {
          field: OtAgentSelectField.ScoutAvailableVersion
        }
      ]
    }).pipe(
      take(1),
      map(res => {
        if(res.data.otAgentsRaw.nodes.length > 0) {
          return res.data.otAgentsRaw.nodes[0].scoutAvailableVersion || '';
        }
        return '';
      })
    );
  }
}
